import { useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useStore } from "./StoreLayout";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";

export default function CustomerAuth({ register = false }) {
  const { slug, store } = useStore();
  const { login, registerCustomer } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect");
  const [isRegister, setIsRegister] = useState(register);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canRegister = store.visitor_settings?.allow_registration;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (isRegister) await registerCustomer({ slug, name: form.name, email: form.email, password: form.password });
      else await login(form.email, form.password);
      navigate(redirect === "checkout" ? `/store/${slug}/checkout` : `/store/${slug}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="max-w-md mx-auto px-6 py-12">
      <Card className="p-8 rounded-2xl border-border">
        <h1 className="text-2xl font-bold tracking-tight">{isRegister ? "Create an account" : "Sign in"}</h1>
        <p className="text-muted-foreground mt-1 text-sm">{store.name}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          {isRegister && (
            <div className="space-y-2"><Label>Name</Label><Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11" data-testid="cust-auth-name" /></div>
          )}
          <div className="space-y-2"><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11" data-testid="cust-auth-email" /></div>
          <div className="space-y-2"><Label>Password</Label><Input type="password" autoComplete={isRegister ? "new-password" : "current-password"} required value={form.password} onChange={(e) => set("password", e.target.value)} className="h-11" data-testid="cust-auth-password" /></div>
          {!isRegister && <div className="text-right -mt-1"><Link to="/forgot-password" className="text-sm text-primary font-medium hover:underline" data-testid="cust-auth-forgot">Forgot password?</Link></div>}
          {error && <p className="text-sm text-destructive" data-testid="cust-auth-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-lg" data-testid="cust-auth-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isRegister ? "Create account" : "Sign in")}
          </Button>
        </form>
        {canRegister && (
          <p className="mt-5 text-sm text-center text-muted-foreground">
            {isRegister ? "Already have an account?" : "New here?"}{" "}
            <button onClick={() => { setIsRegister(!isRegister); setError(""); }} className="text-primary font-medium hover:underline" data-testid="cust-auth-toggle">
              {isRegister ? "Sign in" : "Create an account"}
            </button>
          </p>
        )}
      </Card>
      <p className="mt-4 text-center text-sm"><Link to={`/store/${slug}`} className="text-muted-foreground hover:text-foreground">← Back to store</Link></p>
    </div>
  );
}
